import React, { useMemo, useState, useEffect } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { getProductImageUrl } from '../../../utils/productHelpers'
import { formatCurrency } from '../../../utils/formatters'
import {
  useCancelOrderItemMutation,
  useGetOrderItemDetailsQuery
} from '../../../redux/apis/endpoints/orderItemApi'
import {
  useApproveItemReturnMutation,
  useRejectItemReturnMutation,
  useGetReturnRequestByOrderItemQuery
} from '../../../redux/apis/endpoints/requestApi'
import { toast } from 'react-hot-toast'
import { useGetProductReviewsQuery } from '../../../redux/apis/endpoints/reviewApi'

// Import our new action system
import useActionMatrix from '../../../hooks/useActionMatrix'
import useActionHandler from '../../../hooks/useActionHandler'
import ActionButtonRenderer from '../../actions/shared/ActionButtonRenderer'
import { OrderItemStatusBadge } from '../../actions/shared/StatusBadge'

// Import extracted components and utilities
import { getItemStatusInfo } from './utils/itemStatusUtils'
import RejectModal from './components/RejectModal'
import ConfirmationModal from './components/ConfirmationModal'
import ApproveReturnModal from './components/ApproveReturnModal'
// Import the new cancel modals
import CancelItemModal from '../../actions/shared/modals/CancelItemModal'
import MerchantCancelItemModal from '../../actions/shared/modals/MerchantCancelItemModal'
// Import cancel info display component
import CancelInfoDisplay from '../../actions/shared/CancelInfoDisplay'

const OrderItemCard = ({
  orderItem: propOrderItem,
  fulfillmentGroup,
  order,
  viewContext
}) => {
  // --- ALL HOOKS MUST BE CALLED FIRST ---
  const history = useHistory()
  const user = useSelector(state => state.auth.user)
  const userLang = useSelector(state => state.auth.userLang)

  // Use RTK Query to subscribe to order item data for real-time updates
  // This ensures the component re-renders when cache is invalidated by mutations
  const {
    data: latestOrderItem,
    isLoading: isFetchingItem,
    error: fetchError
  } = useGetOrderItemDetailsQuery(propOrderItem?.id, {
    skip: !propOrderItem?.id // Skip if no ID provided
  })

  // Use the latest data from RTK Query if available, otherwise fall back to props
  const orderItem = latestOrderItem || propOrderItem

  const [cancelOrderItemMutation, { isLoading: isCanceling }] =
    useCancelOrderItemMutation()

  // Query for reviews related to this product and fulfillment group
  const { data: productReviews = [] } = useGetProductReviewsQuery(
    {
      productId: orderItem?.product?.id,
      fulfillmentGroupId: fulfillmentGroup?.id,
      strapiV3: true
    },
    {
      skip:
        !orderItem?.product?.id ||
        !fulfillmentGroup?.id ||
        !(
          fulfillmentGroup.status?.toLowerCase() ===
            'delivery_completed_to_customer' ||
          orderItem.status?.toLowerCase() === 'completed' ||
          orderItem.status?.toLowerCase() === 'returned'
        )
    }
  )

  // Fetch the return request if this item has one - now using populated data
  const returnRequest = orderItem?.returnRequest;

  // Return request mutations
  const [approveItemReturn, { isLoading: isApprovingReturn }] =
    useApproveItemReturnMutation()
  const [rejectItemReturn, { isLoading: isRejectingReturn }] =
    useRejectItemReturnMutation()
  const [rejectionReason, setRejectionReason] = useState('')

  // Determine user role for action matrix
  const userRole = useMemo(() => {
    if (
      viewContext === 'selling' ||
      viewContext === 'lending' ||
      (viewContext === 'all' && order.source === 'merchant')
    ) {
      return 'merchant'
    }
    return 'customer'
  }, [viewContext, order.source])

  // Use our new action matrix system
  const enrichedOrderItem = useMemo(
    () => ({
      ...orderItem,
      fulfillmentGroup: fulfillmentGroup // Ensure fulfillment group is accessible
    }),
    [orderItem, fulfillmentGroup]
  )

  const actionsFromMatrix = useActionMatrix(
    enrichedOrderItem,
    'orderItem',
    userRole
  )

  // MVP: Hide Cancel Item actions (handled at FG level)
  const availableActions = actionsFromMatrix?.filter(a => !a.id?.startsWith('cancel_item_')) || [];

  // Use action handler hook
  const { handleAction, activeModals } = useActionHandler()

  // Legacy modals state (keeping for backward compatibility with existing modals)
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
  const [actionToConfirm, setActionToConfirm] = useState(null)
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false)
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false)
  // New cancel modal states
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false)

  // Filter reviews to find any that match this fulfillment group
  const existingProductReviews = useMemo(() => {
    if (
      !productReviews?.length ||
      !fulfillmentGroup?.id ||
      !orderItem?.product?.id
    )
      return []

    return productReviews.filter(review => {
      // Strict check for fulfillment group relationship
      let matchesFulfillmentGroup = false
      if (typeof review.fulfillmentGroup === 'number') {
        matchesFulfillmentGroup =
          review.fulfillmentGroup === fulfillmentGroup.id
      } else if (review.fulfillmentGroup?.id) {
        matchesFulfillmentGroup =
          review.fulfillmentGroup.id === fulfillmentGroup.id
      }

      // Strict check for product relationship
      let matchesProduct = false
      if (review.products && Array.isArray(review.products)) {
        matchesProduct = review.products.some(p => {
          const productIdToCompare = typeof p === 'number' ? p : p.id
          return productIdToCompare === orderItem.product.id
        })
      }

      return matchesFulfillmentGroup && matchesProduct
    })
  }, [productReviews, fulfillmentGroup?.id, orderItem?.product?.id])

  // Check if we should hide the order item status badge
  // Hide when all order items in the FG have the same status as the FG itself
  const shouldHideItemStatus = useMemo(() => {
    if (
      !fulfillmentGroup?.orderItems ||
      fulfillmentGroup.orderItems.length === 0
    ) {
      return false
    }

    if (!orderItem) {
      return false
    }

    // Check if all order items have the same status as the fulfillment group
    const allItemsMatchFgStatus = fulfillmentGroup.orderItems.every(
      item => item.status === fulfillmentGroup.status
    )

    // Also check if the current item matches the FG status
    const currentItemMatchesFgStatus =
      orderItem.status === fulfillmentGroup.status

    return allItemsMatchFgStatus && currentItemMatchesFgStatus
  }, [
    fulfillmentGroup?.orderItems,
    fulfillmentGroup?.status,
    orderItem?.status
  ])

  useEffect(() => {
    // Debug log for orderItem with return request info
    if (
      orderItem &&
      orderItem.status?.toLowerCase() === 'return_requested_by_customer'
    ) {
      console.log('Return requested order item:', {
        orderItemId: orderItem.id,
        returnRequestId: orderItem.returnRequestId,
        status: orderItem.status,
        returnRequest: returnRequest
      })
    }
  }, [orderItem, returnRequest])

  // Debug logging for cache updates
  React.useEffect(() => {
    if (latestOrderItem && propOrderItem) {
      console.log('🔄 OrderItemCard cache update detected:', {
        itemId: propOrderItem.id,
        propStatus: propOrderItem.status,
        cacheStatus: latestOrderItem.status,
        statusChanged: propOrderItem.status !== latestOrderItem.status,
        hasReturnRequest: !!latestOrderItem.returnRequest
      })
    }
  }, [latestOrderItem, propOrderItem])

  // Handle loading and error states for RTK Query
  if (!orderItem) {
    if (isFetchingItem) {
      return (
        <div className='bg-white border border-gray-200 rounded-lg p-4 shadow-sm w-full max-w-4xl mx-auto'>
          <div className='flex justify-center items-center h-32'>
            <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-brand-teal'></div>
          </div>
        </div>
      )
    }
    if (fetchError) {
      return (
        <div className='bg-white border border-red-200 rounded-lg p-4 shadow-sm w-full max-w-4xl mx-auto'>
          <div className='text-red-600 text-sm'>
            Error loading order item: {fetchError.message}
          </div>
        </div>
      )
    }
    return (
      <div className='p-4 border rounded-md bg-red-50 text-red-700 text-xs'>
        Error: Item data is incomplete.
      </div>
    )
  }

  if (!orderItem.product || !order || !fulfillmentGroup) {
    return (
      <div className='p-4 border rounded-md bg-red-50 text-red-700 text-xs'>
        Error: Item, Fulfillment Group, or Order data is incomplete.
      </div>
    )
  }

  const { product, quantity, price, status, type, rentOption } = orderItem
  const imageUrl = getProductImageUrl(product)
  const orderId = order.id
  const orderCurrency = order.currency || 'USD'
  const fgId = fulfillmentGroup.id

  // Action handling that integrates with our new system
  const handleItemAction = async action => {
    switch (action.id) {
      case 'leave_review_user':
        if (action.targetProductId && fgId) {
          history.push(
            `/fulfillmentgroup/${fgId}/product/${action.targetProductId}/review`,
            {
              orderId: orderId,
              merchantId:
                orderItem.merchant?.id || fulfillmentGroup.merchant?.id,
              existingReview: action.existingReview || null,
              orderItemData: orderItem
            }
          )
        } else {
          toast.error(
            userLang === 'zh-CN'
              ? '无法评价，缺少商品或订单组信息。'
              : 'Cannot leave review, missing product or fulfillment group information.'
          )
        }
        break
      case 'initiate_return_user':
        if (action.targetOrderItemId || orderItem.id) {
          history.push(
            `/dashboard/returns/request/${
              action.targetOrderItemId || orderItem.id
            }`
          )
        }
        break
      case 'reject_return_merchant':
        setActionToConfirm(action)
        setIsRejectModalOpen(true)
        break
      case 'approve_return_merchant':
        setActionToConfirm(action)
        setIsApproveModalOpen(true)
        break
      case 'cancel_item_user':
      case 'cancel_item_user_processing':
      case 'cancel_rental_item':
      case 'cancel_rental_item_processing':
      case 'cancel_item_merchant':
      case 'cancel_item_merchant_processing':
      case 'cancel_rental_item_merchant':
      case 'cancel_rental_item_merchant_processing':
        setActionToConfirm(action)
        setIsCancelModalOpen(true)
        break
      default:
        // Use the new action handler for all other actions
        handleAction(action)
        break
    }
  }

  // Handle cancel item with reason and comments
  const handleCancelItem = async ({ reason, comments }) => {
    try {
      await cancelOrderItemMutation({
        orderItemId: orderItem.id,
        cancelReason: reason,
        cancelComments: comments
      }).unwrap()
      toast.success(
        userLang === 'zh-CN' ? '物品已取消' : 'Item cancelled successfully'
      )
      setIsCancelModalOpen(false)
      setActionToConfirm(null)
    } catch (error) {
      toast.error(
        error.data?.message || error.message || 'Failed to cancel item'
      )
    }
  }

  // Legacy modal handling for specific actions that need custom modals
  const executeConfirmedAction = async () => {
    if (!actionToConfirm) return
    closeConfirmationModal()

    switch (actionToConfirm.id) {
      // Legacy handling - should not be used anymore
      default:
        console.warn('Unhandled confirmed action:', actionToConfirm.id)
        break
    }
    setActionToConfirm(null)
  }

  const handleRejectReturn = async (
    orderItem,
    returnRequest,
    reason,
    rejectMutation,
    closeModal
  ) => {
    try {
      await rejectMutation({
        returnRequestId: returnRequest.id,
        rejectionReason: reason
      }).unwrap()
      toast.success(
        userLang === 'zh-CN' ? '退货请求已拒绝' : 'Return request rejected'
      )
      closeModal()
    } catch (error) {
      toast.error(
        error.data?.message || error.message || 'Failed to reject return'
      )
    }
  }

  const handleApproveReturn = async (
    orderItem,
    returnRequest,
    approveMutation,
    closeModal
  ) => {
    try {
      await approveMutation({
        returnRequestId: returnRequest.id
      }).unwrap()
      toast.success(
        userLang === 'zh-CN' ? '退货请求已批准' : 'Return request approved'
      )
      closeModal()
    } catch (error) {
      toast.error(
        error.data?.message || error.message || 'Failed to approve return'
      )
    }
  }

  const closeConfirmationModal = () => {
    setIsConfirmModalOpen(false)
    setActionToConfirm(null)
  }

  const closeRejectModal = () => {
    setIsRejectModalOpen(false)
    setRejectionReason('')
    setActionToConfirm(null)
  }

  const closeApproveModal = () => {
    setIsApproveModalOpen(false)
    setActionToConfirm(null)
  }

  const closeCancelModal = () => {
    setIsCancelModalOpen(false)
    setActionToConfirm(null)
  }

  // Modal handler functions for reject and approve actions
  const handleRejectConfirm = async () => {
    await handleRejectReturn(
      orderItem,
      returnRequest,
      rejectionReason,
      rejectItemReturn,
      closeRejectModal
    )
  }

  const handleApproveConfirm = async () => {
    await handleApproveReturn(
      orderItem,
      returnRequest,
      approveItemReturn,
      closeApproveModal
    )
  }

  return (
    <div className='bg-white border border-gray-200 rounded-lg p-4 shadow-sm w-full max-w-4xl mx-auto'>
      {/* Main flex container for content and actions/status */}
      <div className='flex flex-col sm:flex-row sm:justify-between gap-4'>
        {/* Wrapper for Image and Text Details */}
        <div className='flex flex-row gap-4 flex-grow'>
          <Link to={`/product/${product.id}`} className='flex-shrink-0'>
            <img
              src={imageUrl}
              alt={product.name}
              className='w-24 h-24 sm:w-32 sm:h-32 rounded-md object-cover hover:opacity-90 transition-opacity'
            />
          </Link>
          <div className='flex-grow text-right sm:text-left'>
            <Link to={`/product/${product.id}`} className='hover:underline'>
              <h4 className='text-md sm:text-lg font-semibold text-gray-800 hover:text-brand-teal transition-colors'>
                {product.name}
              </h4>
            </Link>
            <p className='text-sm text-gray-700'>
              {userLang === 'zh-CN' ? '数量: ' : 'Quantity: '}
              {quantity}
            </p>
            {product.brand && (
              <p className='text-xs text-gray-500 mb-0.5'>
                {userLang === 'zh-CN' ? '品牌: ' : 'Brand: '}
                {product.brand}
              </p>
            )}
            {orderItem.variantInfo && (
              <p className='text-xs text-gray-500 mb-0.5'>
                {typeof orderItem.variantInfo === 'string'
                  ? Object.entries(JSON.parse(orderItem.variantInfo))
                      .map(
                        ([key, value]) =>
                          `${
                            key.charAt(0).toUpperCase() + key.slice(1)
                          }: ${value}`
                      )
                      .join(', ')
                  : 'Variant details unavailable'}
              </p>
            )}

            {type && (
              <div className='flex gap-2 justify-end sm:justify-start'>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    type === 'buy'
                      ? 'bg-blue-100 text-blue-800'
                      : type === 'rent'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {type === 'buy' &&
                    userRole === 'customer' &&
                    (userLang === 'zh-CN' ? '购买' : 'Buy')}
                  {type === 'buy' &&
                    userRole === 'merchant' &&
                    (userLang === 'zh-CN' ? '销售' : 'Sell')}
                  {type === 'rent' &&
                    userRole === 'customer' &&
                    (userLang === 'zh-CN' ? '租赁' : 'Rent')}
                  {type === 'rent' &&
                    userRole === 'merchant' &&
                    (userLang === 'zh-CN' ? '出租' : 'Lend')}
                  {type !== 'buy' &&
                    type !== 'rent' &&
                    (userLang === 'zh-CN' ? '其他' : 'Other')}
                </span>
              </div>
            )}
            {type === 'buy' ? (
              <p className='text-sm text-gray-700'>
                {formatCurrency(price, orderCurrency)}
              </p>
            ) : (
              type === 'rent' &&
              rentOption && (
                <div className='mt-2 text-sm text-gray-700'>
                  <p>
                    {formatCurrency(rentOption.price || price, orderCurrency)}
                    {userLang === 'zh-CN' ? ' 为期 ' : ' for '}
                    {rentOption.duration} {rentOption.option}
                    {rentOption.duration > 1 ? 's' : ''}
                  </p>
                  {orderItem.securityDeposit && orderItem.securityDeposit > 0 && (
                    <p className='text-xs text-gray-500'>
                      (+{' '}
                      {formatCurrency(orderItem.securityDeposit, orderCurrency)}{' '}
                      {userLang === 'zh-CN' ? '押金' : 'deposit'})
                    </p>
                  )}
                </div>
              )
            )}
          </div>
        </div>

        {/* Actions Column - only render if there are actions available */}
        <div className=' flex flex-col items-stretch sm:items-end flex-shrink-0 w-full sm:w-auto'>
          {/* Status Badge next to Type Badge */}
          {!shouldHideItemStatus && (
            <OrderItemStatusBadge
              orderItem={orderItem}
              className='pl-2.5 mb-2'
              size='sm'
            />
          )}
          {availableActions && availableActions.length > 0 && (
            <ActionButtonRenderer
              actions={availableActions}
              layout='vertical'
              size='sm'
              onActionClick={handleItemAction}
              isLoading={activeModals.length > 0 || isCanceling}
              className='space-y-2 pt-3'
            />
          )}
        </div>
      </div>

      {/* Cancel Information Display */}
      <CancelInfoDisplay
        entity={orderItem}
        entityType='item'
        fulfillmentGroup={fulfillmentGroup}
        className='mt-4'
        size='sm'
      />

      {/* Legacy Confirmation Modal - keeping for specific actions that need custom modals */}
      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        onClose={closeConfirmationModal}
        onConfirm={executeConfirmedAction}
        title={actionToConfirm?.label || ''}
        message={actionToConfirm?.message || ''}
        isLoading={isCanceling}
      />

      {/* Reject Modal for return rejection */}
      <RejectModal
        isOpen={isRejectModalOpen}
        onClose={closeRejectModal}
        rejectionReason={rejectionReason}
        setRejectionReason={setRejectionReason}
        onConfirm={handleRejectConfirm}
        isLoading={isRejectingReturn}
      />

      {/* Approve Modal for return approval */}
      <ApproveReturnModal
        isOpen={isApproveModalOpen}
        onClose={closeApproveModal}
        onConfirm={handleApproveConfirm}
        isLoading={isApprovingReturn}
        orderItem={orderItem}
        returnRequest={returnRequest}
      />

      {/* Cancel Item Modals */}
      {userRole === 'customer' ? (
        <CancelItemModal
          isOpen={isCancelModalOpen}
          onClose={closeCancelModal}
          onConfirm={handleCancelItem}
          isLoading={isCanceling}
          entity={orderItem}
        />
      ) : (
        <MerchantCancelItemModal
          isOpen={isCancelModalOpen}
          onClose={closeCancelModal}
          onConfirm={handleCancelItem}
          isLoading={isCanceling}
          entity={orderItem}
        />
      )}
    </div>
  )
}

export default OrderItemCard
